import { useState } from "react";
import { ArrowRight, CheckCircle2, ExternalLink, ImageOff, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, LargeImageDetails } from "@/types/technicalAudit";
import {
	readSuggestionList,
	readSuggestionObject,
	readSuggestionString,
} from "./suggestionUtils";

interface LargeImageCardProps {
	issue: PageIssue;
}

function formatBytes(bytes: number): string {
	if (!bytes || bytes < 0) return "—";
	if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
	return `${(bytes / 1024).toFixed(0)} KB`;
}

// The backend optimize step returns original_size/new_size/bytes_saved/
// new_dimensions/still_over_max/image_context, but the card is rendered as
// `<LargeImageCard issue={issue} />` from IssueDiffDrawer with NO result prop and
// PageIssue has no typed apply-result field. Read a result defensively if one is
// ever persisted onto the issue so the post-apply delta lights up WITHOUT
// fabricating numbers when it is absent.
// TODO: thread/persist apply result (original_size/new_size/bytes_saved/new_dimensions/still_over_max)
// so the post-apply delta shows here.
interface LargeImageApplyResult {
	image_context?: string;
	error?: string;
	original_size?: number;
	new_size?: number;
	bytes_saved?: number;
	new_dimensions?: string | null;
	still_over_max?: boolean;
}

function readApplyResult(issue: PageIssue): LargeImageApplyResult | null {
	const bag = issue as unknown as Record<string, unknown>;
	const candidates = [bag.fix_result, bag.apply_result, bag.result];
	for (const candidate of candidates) {
		if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
			continue;
		}
		const o = candidate as Record<string, unknown>;
		const newSize = typeof o.new_size === "number" ? o.new_size : undefined;
		const error = typeof o.error === "string" ? o.error : undefined;
		// Only treat as a real result when there is a concrete outcome to show.
		if ((newSize && newSize > 0) || error) {
			return {
				image_context:
					typeof o.image_context === "string" ? o.image_context : undefined,
				error,
				original_size:
					typeof o.original_size === "number" ? o.original_size : undefined,
				new_size: newSize,
				bytes_saved: typeof o.bytes_saved === "number" ? o.bytes_saved : undefined,
				new_dimensions:
					typeof o.new_dimensions === "string" ? o.new_dimensions : null,
				still_over_max:
					typeof o.still_over_max === "boolean" ? o.still_over_max : undefined,
			};
		}
	}
	return null;
}

export function LargeImageCard({ issue }: LargeImageCardProps) {
	const details = issue.issue_details as LargeImageDetails;
	const [imageError, setImageError] = useState(false);
	const [dimensions, setDimensions] = useState<{ w: number; h: number } | null>(
		null,
	);

	const fileSize = details.file_size_bytes || 0;
	const maxSize = details.max_size_bytes || 0;
	const isOverCap = maxSize > 0 && fileSize > maxSize;
	const overRatio = maxSize > 0 ? fileSize / maxSize : 0;

	// Bar math (guarded): scaling to max(maxSize*2, fileSize*1.2) keeps a sub-cap
	// file rendering below the threshold marker instead of a misleading near-full bar.
	const maxDisplay = Math.max(maxSize * 2, fileSize * 1.2, 1);
	const percentage = Math.min((fileSize / maxDisplay) * 100, 100);
	const thresholdPercent = Math.min((maxSize / maxDisplay) * 100, 100);

	const applyResult = readApplyResult(issue);

	// Predict compressible-vs-protected from content_type; confirm post-apply.
	const predictedCompressible = issue.content_type === "post";
	const confirmedCompressible =
		applyResult?.image_context === "blog" ||
		(typeof applyResult?.new_size === "number" &&
			applyResult.new_size > 0 &&
			!applyResult.error);
	const confirmedProtected =
		Boolean(applyResult?.error) &&
		(applyResult?.image_context === "page" ||
			applyResult?.image_context === "unknown" ||
			/protected|landing|service page/i.test(applyResult?.error || ""));
	const isCompressible =
		confirmedCompressible || (!confirmedProtected && predictedCompressible);

	const suggestion = readSuggestionObject(issue);
	const recommendation = readSuggestionString(suggestion, "recommendation");
	const reasoning = readSuggestionString(suggestion, "reasoning");
	const steps = readSuggestionList(suggestion, "optimization_steps");

	const filename = (() => {
		try {
			return new URL(details.image_url).pathname.split("/").pop() || details.image_url;
		} catch {
			return details.image_url.split("/").pop()?.split("?")[0] || details.image_url;
		}
	})();

	const savedPct =
		applyResult?.original_size && applyResult.new_size
			? Math.round((1 - applyResult.new_size / applyResult.original_size) * 100)
			: null;

	return (
		<div className="space-y-5">
			{/* ① Image hero — see the actual target, uncropped (object-contain letterbox) */}
			<figure className="space-y-2">
				<div className="relative w-full overflow-hidden rounded-xl border bg-muted">
					<div className="flex max-h-[360px] min-h-[180px] w-full items-center justify-center">
						{imageError ? (
							<div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-muted-foreground">
								<ImageOff className="h-8 w-8" />
								<span className="text-xs">
									Preview unavailable — the file may still be fine (hotlink/CORS
									protected).
								</span>
							</div>
						) : (
							<img
								src={details.image_url}
								alt="Target image being compressed"
								loading="lazy"
								className="max-h-[360px] w-auto max-w-full object-contain"
								onLoad={(e) =>
									setDimensions({
										w: e.currentTarget.naturalWidth,
										h: e.currentTarget.naturalHeight,
									})
								}
								onError={() => setImageError(true)}
							/>
						)}
					</div>
					<span
						className={cn(
							"absolute right-2 top-2 rounded-md px-2 py-1 text-xs font-semibold shadow-sm",
							isOverCap ? "bg-destructive text-white" : "bg-teal-dark text-white",
						)}
					>
						{formatBytes(fileSize)}
					</span>
				</div>
				<figcaption className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
					<span className="truncate font-mono" title={filename}>
						{filename}
					</span>
					<span className="flex shrink-0 items-center gap-3">
						<span>{dimensions ? `${dimensions.w}×${dimensions.h}px` : "—"}</span>
						<a
							href={details.image_url}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 text-coral hover:text-coral-hover"
						>
							Open original
							<ExternalLink className="h-3 w-3" />
						</a>
					</span>
				</figcaption>
			</figure>

			{/* ② Status verdict — compressible (blog) vs protected (page asset) */}
			{isCompressible ? (
				<div className="flex items-start gap-2 rounded-lg border border-teal/30 bg-teal-light px-3 py-2.5">
					<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-dark" />
					<div className="text-sm text-teal-dark">
						<span className="font-semibold">Compressible — blog post image.</span>{" "}
						Recompressed + resized in place (same URL), targeting ≤{" "}
						{formatBytes(maxSize)}. Aspect ratio is preserved (width-capped, no crop).
					</div>
				</div>
			) : (
				<div className="flex items-start gap-2 rounded-lg border border-coral/30 bg-coral-light px-3 py-2.5">
					<Lock className="mt-0.5 h-4 w-4 shrink-0 text-coral-dark" />
					<div className="text-sm text-coral-dark">
						<span className="font-semibold">Protected — review-only.</span> This looks
						like a landing/service-page asset. Designed page images are not
						auto-compressed; resize/replace it manually or swap to WebP.
					</div>
				</div>
			)}

			{/* ③ Stat grid — calm tiles instead of a cramped one-liner */}
			<dl className="grid grid-cols-2 gap-3">
				<div className="rounded-lg border bg-card p-3">
					<dt className="text-xs uppercase tracking-wide text-muted-foreground">
						Current size
					</dt>
					<dd
						className={cn(
							"mt-1 text-2xl font-bold",
							isOverCap ? "text-destructive" : "text-foreground",
						)}
					>
						{formatBytes(fileSize)}
					</dd>
				</div>
				<div className="rounded-lg border bg-card p-3">
					<dt className="text-xs uppercase tracking-wide text-muted-foreground">
						Max allowed
					</dt>
					<dd className="mt-1 text-2xl font-bold text-foreground">
						{formatBytes(maxSize)}
					</dd>
				</div>
				<div className="rounded-lg border bg-card p-3">
					<dt className="text-xs uppercase tracking-wide text-muted-foreground">
						Over cap
					</dt>
					<dd
						className={cn(
							"mt-1 text-sm font-semibold",
							isOverCap ? "text-destructive" : "text-teal-dark",
						)}
					>
						{isOverCap
							? `≈${overRatio >= 10 ? Math.round(overRatio) : overRatio.toFixed(1)}× threshold`
							: "Within limit"}
					</dd>
				</div>
				<div className="rounded-lg border bg-card p-3">
					<dt className="text-xs uppercase tracking-wide text-muted-foreground">
						Dimensions
					</dt>
					<dd className="mt-1 text-sm font-semibold text-foreground">
						{dimensions ? `${dimensions.w}×${dimensions.h}` : "—"}
					</dd>
				</div>
			</dl>

			{/* ④ Threshold bar — over → destructive, ok → teal; marker = cap */}
			<div className="space-y-1.5">
				<div className="relative">
					<div className="h-3 w-full overflow-hidden rounded-full bg-muted">
						<div
							className={cn(
								"h-full rounded-full transition-all",
								isOverCap ? "bg-destructive" : "bg-teal",
							)}
							style={{ width: `${percentage}%` }}
						/>
					</div>
					<div
						className="absolute top-0 -mt-1 h-5 w-0.5 bg-foreground/60"
						style={{ left: `${thresholdPercent}%` }}
					/>
				</div>
				<p className="text-[11px] text-muted-foreground">
					Marker shows the {formatBytes(maxSize)} cap.
				</p>
			</div>

			{/* ⑤ Post-apply delta — renders ONLY when a real apply result is present */}
			{applyResult &&
			applyResult.new_size &&
			applyResult.new_size > 0 &&
			!applyResult.error ? (
				<div className="space-y-2 rounded-lg border border-teal/30 bg-teal-light p-3">
					<div className="flex items-center gap-3">
						<span className="text-sm font-medium text-muted-foreground">
							{formatBytes(applyResult.original_size || fileSize)}
						</span>
						<ArrowRight className="h-4 w-4 shrink-0 text-teal-dark" />
						<span className="text-sm font-bold text-teal-dark">
							{formatBytes(applyResult.new_size)}
						</span>
						{savedPct !== null && (
							<span className="ml-auto text-xs font-semibold text-teal-dark">
								▼ {savedPct}% · {formatBytes(applyResult.bytes_saved || 0)} saved
							</span>
						)}
					</div>
					{applyResult.new_dimensions && (
						<p className="text-xs text-teal-dark">
							Resized to {applyResult.new_dimensions} (aspect ratio preserved).
						</p>
					)}
					{applyResult.still_over_max && (
						<p className="rounded-md bg-coral-light px-2 py-1.5 text-xs text-coral-dark">
							Still above the {formatBytes(maxSize)} cap after compression — consider
							converting to WebP (see the Non-Next-Gen Image fix) or a manual resize.
						</p>
					)}
				</div>
			) : applyResult?.error ? (
				<div className="rounded-lg border border-coral/30 bg-coral-light p-3 text-xs text-coral-dark">
					<p className="font-semibold">No change applied</p>
					<p className="mt-1">{applyResult.error}</p>
					<p className="mt-1">
						If this is a PNG or already-optimized image, it may not shrink —
						converting to WebP (the Non-Next-Gen Image fix) is usually the better
						lever.
					</p>
				</div>
			) : null}

			{/* ⑥ Recommendation — demoted to a collapsible, secondary block */}
			<details className="rounded-md border bg-muted/40 px-3 py-2.5 text-sm">
				<summary className="cursor-pointer font-medium text-foreground">
					Recommended image fix
				</summary>
				<div className="mt-2 space-y-2">
					{recommendation || steps.length > 0 || reasoning ? (
						<>
							{recommendation && (
								<p className="text-sm text-muted-foreground">{recommendation}</p>
							)}
							{steps.length > 0 && (
								<ul className="space-y-1.5">
									{steps.map((item) => (
										<li
											key={item}
											className="rounded-md border bg-card px-3 py-2 text-sm text-muted-foreground"
										>
											{item}
										</li>
									))}
								</ul>
							)}
							{reasoning && (
								<p className="text-xs text-muted-foreground">{reasoning}</p>
							)}
						</>
					) : (
						<p className="text-sm text-muted-foreground">
							No generated recommendation yet. Use Generate fix above to create a
							compression or replacement plan.
						</p>
					)}
				</div>
			</details>
		</div>
	);
}
